/**
 * player_models.ts — the scanned player-avatar roster (Higgsfield GLBs,
 * generations goal 2026-07-22).
 *
 * A traveler may wear one of these instead of the primitive body templates.
 * The choice replicates to EVERYONE through the avatar row's `attachments`
 * record (`{ model: 'avatar:player_…' }`) — the server's P1 validation
 * already accepts arbitrary point→ref strings, so no schema change; remotes
 * resolve the prefab key to a render asset and wear the GLB.
 */
import type { DbConnection } from '../module_bindings/index.js';
import { lightRigInForce } from './player_clips.js';

export const PLAYER_MODELS: Array<{ key: string; label: string }> = [
  { key: 'avatar:player_pathfinder', label: 'PATHFINDER' },
  { key: 'avatar:player_circuitwright', label: 'CIRCUITWRIGHT' },
  { key: 'avatar:player_skywarden', label: 'SKYWARDEN' },
  { key: 'avatar:player_verdant', label: 'VERDANT' },
  { key: 'avatar:player_nightcourier', label: 'NIGHTCOURIER' },
  // 20-preset wave (2026-07-24) — fifteen more scanned humans.
  { key: 'avatar:player_medica', label: 'MEDICA' },
  { key: 'avatar:player_forgemaster', label: 'FORGEMASTER' },
  { key: 'avatar:player_tidewalker', label: 'TIDEWALKER' },
  { key: 'avatar:player_archivist', label: 'ARCHIVIST' },
  { key: 'avatar:player_duskranger', label: 'DUSKRANGER' },
  { key: 'avatar:player_stellarch', label: 'STELLARCH' },
  { key: 'avatar:player_gearhart', label: 'GEARHART' },
  { key: 'avatar:player_lumenweaver', label: 'LUMENWEAVER' },
  { key: 'avatar:player_quarrier', label: 'QUARRIER' },
  { key: 'avatar:player_skysong', label: 'SKYSONG' },
  { key: 'avatar:player_voltkeeper', label: 'VOLTKEEPER' },
  { key: 'avatar:player_frostrunner', label: 'FROSTRUNNER' },
  { key: 'avatar:player_emberchef', label: 'EMBERCHEF' },
  { key: 'avatar:player_starling', label: 'STARLING' },
  { key: 'avatar:player_stonewright', label: 'STONEWRIGHT' },
];

/** Portrait thumbnail (vite public/) for a preset card — the source Higgsfield
 *  render, downscaled by tools/asset-pipeline/make-avatar-thumbs.mjs. */
export function modelThumbPath(key: string): string {
  return `/avatars/${key.slice('avatar:player_'.length)}.png`;
}

/** The scanned-model key an appearance wears, or null for primitive bodies. */
export function modelKeyOf(attachments: Record<string, string> | null | undefined): string | null {
  const k = attachments?.model;
  return typeof k === 'string' && k.startsWith('avatar:player_') ? k : null;
}

/**
 * The turn a worn player model takes inside its avatar (2026-10-07, "my avatar is backwards when you zoom out"). The
 * scanned bodies face +Z (LOOKED at: the archivist's face is the +Z view), and a player's forward is −Z (the camera
 * looks down −Z at yaw 0; forward = (−sin yaw, −cos yaw)) — so every worn body, mine in third person and every remote
 * player's, walked with its back to its heading. The traveler screen's turntable has its own camera and is untouched.
 */
export const PLAYER_MODEL_YAW = Math.PI;

/**
 * THE SWITCH (player wave 5, 2026-10-08) — the ONE place that decides which registry keys a player body resolves to.
 * An avatar row always holds `avatar:player_<name>`; what that name DRAWS is one of three key sets:
 *   · 'live'  (the default)  `avatar:player_<name>` · `avatar-rig:player_<name>` · `avatar-rig:playerlite_<name>`
 *   · 'wave5' the candidates `avatar:player5_<name>` · `avatar-rig:player5_<name>` · `avatar-rig:player5lite_<name>`
 *   · 'old'   the backups a swap leaves behind: `…player4_<name>` (the bodies as they were before it)
 * Chosen by `localStorage atlas:player-bodies` ('wave5' | 'old'), READ ONCE at load: it is how the regenerated bodies
 * are looked at in the game before they replace anything, and one client's way back afterwards. A set whose keys are
 * not registered on a shard is simply not in force there (the live keys answer).
 */
export type PlayerBodySet = 'live' | 'wave5' | 'old';
function readPlayerBodySet(): PlayerBodySet {
  try { const v = (globalThis as { localStorage?: Storage }).localStorage?.getItem('atlas:player-bodies'); return v === 'wave5' ? 'wave5' : v === 'old' ? 'old' : 'live'; }
  catch { return 'live'; }
}
export const PLAYER_BODY_SET: PlayerBodySet = readPlayerBodySet();
const BODY_TAG: Record<PlayerBodySet, [string, string]> = { live: ['player_', 'playerlite_'], wave5: ['player5_', 'player5lite_'], old: ['player4_', 'player4lite_'] };
/** The three keys of a body (`key` = the avatar row's `avatar:player_<name>`): its static twin, its FULL rig, its LIGHT rig. */
export function playerBodyKeys(key: string, set: PlayerBodySet = PLAYER_BODY_SET): { static: string; rig: string; rigLight: string } {
  const name = key.slice('avatar:player_'.length), [tag, lite] = BODY_TAG[set];
  return { static: `avatar:${tag}${name}`, rig: `avatar-rig:${tag}${name}`, rigLight: `avatar-rig:${lite}${name}` };
}
/** R8: the RIGGED twin of a scanned body (`avatar:player_x` → `avatar-rig:player_x`, or the switched set's). */
export function rigKeyOf(key: string): string { return playerBodyKeys(key).rig; }

/** A prefab's catalog row (asset id + the default scale it was registered with), or null until the catalog lands. */
export function prefabRow(conn: DbConnection, prefabKey: string): { assetId: bigint; defaultScale: number } | null {
  for (const pm of conn.db.prefabModel.iter()) {
    if (pm.prefabKey === prefabKey) return { assetId: pm.assetId, defaultScale: pm.defaultScale };
  }
  return null;
}

/** Resolve a prefab key to its render asset id (null until the catalog lands). */
export function prefabAssetId(conn: DbConnection, prefabKey: string): bigint | null {
  for (const pm of conn.db.prefabModel.iter()) {
    if (pm.prefabKey === prefabKey) return pm.assetId;
  }
  return null;
}

/** A registered, usable row (asset 0 is the catalog's "no model"), or null. */
function usablePrefabRow(conn: DbConnection, prefabKey: string): { assetId: bigint; defaultScale: number } | null {
  const r = prefabRow(conn, prefabKey);
  return r && r.assetId !== 0n ? r : null;
}
/** THE SWITCH: the static twin a player body draws — the switched set's when it is registered, else the live key's. */
export function playerStaticAssetId(conn: DbConnection, key: string): bigint | null {
  const k = playerBodyKeys(key).static;
  if (k !== key) { const r = usablePrefabRow(conn, k); if (r) return r.assetId; }
  return prefabAssetId(conn, key);
}
/**
 * THE SWITCH: a player body's rig rows — `full` (the one rig every body has today) and `light` (null until one is
 * registered: the caller then wears `full` for everything, as before the wave). A switched set whose FULL rig is not
 * registered is not in force for this body.
 */
export function playerRigRows(conn: DbConnection, key: string): { full: { assetId: bigint; defaultScale: number } | null; light: { assetId: bigint; defaultScale: number } | null } {
  const live = playerBodyKeys(key, 'live'), set = playerBodyKeys(key);
  if (set.rig !== live.rig) { const full = usablePrefabRow(conn, set.rig); if (full) { const light = usablePrefabRow(conn, set.rigLight); return { full, light: lightRigInForce(full, light) ? light : null }; } }
  // THE LIGHT ROW IS IN FORCE ONLY BESIDE ITS OWN FULL RIG (lib/player_clips.ts lightRigInForce — the swap's --undo case)
  const full = prefabRow(conn, live.rig), light = usablePrefabRow(conn, live.rigLight);
  return { full, light: lightRigInForce(full, light) ? light : null };
}
